import { ExpandableText } from "@/core/components/expandable-text";
import { Stack } from "@/core/components/ui/stack";
import { ActionRowGlyph, ActionRowHeader } from "@/accounts-payable/common";
import type { ApAgentAction } from "@/accounts-payable/types";
import { actionDetail, isPendingReviewAction } from "@/accounts-payable/utils";
import { cn } from "@/core/utils/utils";

/**
 * One agent action as a checklist row — the same shape pending or resolved:
 * status glyph, a title in primary, the target in secondary (the header is
 * shared with the review page). Pending rows add the confidence and the
 * agent's reasoning; resolved rows stay quiet and only speak up with who
 * decided or when something went off-plan. Approving happens on the review
 * page.
 */
export const ActionChecklistRow = ({
  action,
  showDivider,
}: {
  action: ApAgentAction;
  showDivider: boolean;
}) => {
  const pending = isPendingReviewAction(action);

  return (
    <Stack
      direction="row"
      gap="sm"
      align="start"
      fullWidth
      className={cn("py-3", {
        "border-t border-stroke-tertiary": showDivider,
      })}
    >
      <ActionRowGlyph action={action} />
      <Stack gap="sm" className="min-w-0 flex-1">
        <ActionRowHeader action={action} showTarget />
        {/* The reasoning supports a proposal still to decide, so it's the
            quietest line and clamps to two; the review page has all of it. */}
        {pending &&
          action.rationale &&
          action.rationale !== actionDetail(action) && (
            <ExpandableText
              text={action.rationale}
              maxLines={2}
              textColor="tertiary"
              gradientColor="hsl(var(--card))"
            />
          )}
      </Stack>
    </Stack>
  );
};
